import { ActionIcon, Button, Grid, Group, Paper, Text } from '@mantine/core';
import { Dropzone, DropzoneStatus } from '@mantine/dropzone';
import { showNotification } from '@mantine/notifications';
import { useRef } from 'react';
import { Trash, X } from 'tabler-icons-react';

interface Props {
  maxNumFiles?: number;
  label: string;
  currentUploadedFiles: File[];
  removeListItem: (index: number) => void;
  addListItem: (droppedFiles: File[]) => void;
}

function UploadButton(props: Props) {
  const {
    maxNumFiles,
    label,
    currentUploadedFiles,
    removeListItem,
    addListItem,
  } = props;
  const openRef = useRef<() => void>();
  const MAX_FILE_SIZE_BYTES = 26214400;
  const MAX_FILE_SIZE_MB = MAX_FILE_SIZE_BYTES / 1048576;

  const dropzoneChildren = (status: DropzoneStatus) => (
    <Group
      position='center'
      spacing='xl'
      style={{ pointerEvents: 'none', display: 'none' }}
    ></Group>
  );

  const MAX_LENGTH = maxNumFiles || 20;

  return (
    <>
      <Text style={{ fontSize: 14 }}>{label}</Text>

      <Paper withBorder p={5}>
        <Grid align='center'>
          <Dropzone
            // @ts-ignore
            openRef={openRef}
            className='hidden-file-upload-button'
            maxSize={MAX_FILE_SIZE_BYTES}
            accept={['.pem']}
            onDrop={(files: File[]) => {
              if (files.length + currentUploadedFiles.length > MAX_LENGTH) {
                showNotification({
                  id: 'max-update-file-uploads-reached',
                  message: `Cannot upload more than ${MAX_LENGTH} files`,
                  icon: <X size={16} />,
                  color: 'red',
                  autoClose: 10000,
                });
                return;
              }

              addListItem(files);
            }}
            onReject={(fileRejections) => {
              const reject = fileRejections[0];
              const fileName = reject.file.name;
              const fileNameLength = reject.file.name.length;
              let fileNameDisplay = fileName;

              if (fileNameLength > 25) {
                fileNameDisplay =
                  fileName.substring(0, 14) +
                  '...' +
                  fileName.substring(fileNameLength - 8, fileNameLength);
              }
              showNotification({
                id: 'update-file-upload-reject',
                message: `${fileNameDisplay} was not uploaded: ${
                  reject.errors[0].code === 'file-too-large'
                    ? `File is larger than ${MAX_FILE_SIZE_MB} MB`
                    : reject.errors[0].message
                }`,
                icon: <X size={16} />,
                color: 'red',
              });
            }}
          >
            {(status) => dropzoneChildren(status)}
          </Dropzone>

          <Grid.Col xs={12} md={6}>
            <Button
              onClick={() => openRef.current && openRef.current()}
              color='gray'
              fullWidth
            >
              Select file
            </Button>
          </Grid.Col>
        </Grid>
      </Paper>
      <Text
        className='truncate'
        style={{ fontSize: 12 }}
        color='gray'
        align='left'
        px={5}
      >
        {currentUploadedFiles.length > 0
          ? currentUploadedFiles.map(
              (file: File | undefined, index: number) => (
                <Group
                  key={index}
                  direction='row'
                  align='flex-end'
                  spacing='xs'
                  noWrap
                >
                  <ActionIcon
                    color='red'
                    variant='hover'
                    onClick={() => removeListItem(index)}
                    data-cy='removeFile'
                  >
                    <Trash />
                  </ActionIcon>
                  <Text
                    className='truncate'
                    style={{ fontSize: 14 }}
                    color='gray'
                    align='left'
                  >
                    {file?.name}
                  </Text>
                </Group>
              )
            )
          : ' '}
      </Text>
    </>
  );
}

export default UploadButton;
